import React, { useState } from 'react'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import SearchIcon from '@mui/icons-material/Search'
import StartOutlined from '@mui/icons-material/StartOutlined'

import { useSearchForAssetHolderQuery } from '../../../../api-services/asset-holdings/assetHolderApi'
import { useCreateAccountChargeExclusionMutation } from '../../../../api-services/asset-holdings/accountChargeRunApi'
import { type AssetHoldersSearchRequest } from '../../../../api-schema-types/asset-holdings/AssetHolderPayloadDto'
import { type AssetHolderSummary } from '../../../../api-schema-types/asset-holdings/AssetHolderDomainDtos'
import { RtkQueryHelper } from '../../../../api-services/shared/RtkQueryHelper'
import { useAppDispatch } from '../../../../app/hooks'
import { setNotification } from '../../../components/ui-widgets/uiWidgetsSlice'
import LoadingComponent from '../../../components/ui-widgets/LoadingComponent'
import ErrorComponent from '../../../components/ui-widgets/ErrorComponent'
import ButtonLoadingComponent from '../../../../views/components/shared/ButtonLoadingComponent'

const MIN_REASON_LENGTH = 3

const AddAccountChargeExclusionDialog: React.FC = () => {
  const dispatch = useAppDispatch()

  const [isDialogOpen, toggleDialog] = useState<boolean>(false)

  const [searchQuery, setSearchQuery] = useState<AssetHoldersSearchRequest>({ searchInput: '' })
  const searchResult = useSearchForAssetHolderQuery(searchQuery, { skip: searchQuery.searchInput === '' })
  const [selectedAssetHolder, setSelectedAssetHolder] = useState<AssetHolderSummary | null>(null)
  const [reason, setReason] = useState<string>('')

  const [apiCreateExclusion, { isLoading }] = useCreateAccountChargeExclusionMutation()

  const handleSearchQueryChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const input: string = event.target.value
    if (input.length >= 3 || input.length === 0) {
      setSearchQuery({ searchInput: input })
    }
  }

  const closeDialog = (): void => {
    toggleDialog(false)
    setSearchQuery({ searchInput: '' })
    setSelectedAssetHolder(null)
    setReason('')
  }

  const canSubmit = selectedAssetHolder !== null && reason.trim().length >= MIN_REASON_LENGTH && !isLoading

  const handleSubmit = (): void => {
    if (selectedAssetHolder === null) {
      return
    }
    apiCreateExclusion({ assetHolderId: selectedAssetHolder.id, reason: reason.trim() })
      .unwrap()
      .then(() => {
        dispatch(setNotification({ open: true, severity: 'success', message: 'Member has been excluded from the account charge run.' }))
        closeDialog()
      })
      .catch(error => {
        dispatch(setNotification({ open: true, severity: 'error', message: RtkQueryHelper.mapResponseError(error) }))
      })
  }

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', flex: 15 },
    {
      field: 'firstName',
      headerName: 'First Name',
      flex: 30,
      renderCell: params => (<Tooltip title={params.value}><p>{params.value}</p></Tooltip>)
    },
    {
      field: 'lastName',
      headerName: 'Last Name',
      flex: 30,
      renderCell: params => (<Tooltip title={params.value}><p>{params.value}</p></Tooltip>)
    },
    {
      field: 'username',
      headerName: 'Email Address',
      flex: 60,
      renderCell: params => (<Tooltip title={params.value}><p>{params.value}</p></Tooltip>)
    },
    {
      field: 'select',
      headerName: '',
      flex: 15,
      sortable: false,
      renderCell: params => (
        <Button variant="text" onClick={() => { setSelectedAssetHolder(params.row as AssetHolderSummary) }}>
          <StartOutlined />
        </Button>
      )
    }
  ]

  return (
    <>
      <Button variant={'contained'} color={'primary'} onClick={() => { toggleDialog(true) }}>
        Add Member
      </Button>

      <Dialog open={isDialogOpen} maxWidth={'md'} fullWidth={true} onClose={closeDialog}>
        <DialogTitle>Exclude Member from Account Charge Run</DialogTitle>
        <DialogContent>
          <Typography variant={'subtitle1'} sx={{ mt: 1, mb: 2 }}>1. Select member</Typography>

          {selectedAssetHolder === null
            ? (
            <>
              <TextField
                label="Search by proxima id, first name, last name or email address."
                type={'search'}
                sx={{ width: '100%', mb: 2 }}
                InputProps={{
                  endAdornment: (<InputAdornment position="end"><SearchIcon /></InputAdornment>)
                }}
                onChange={handleSearchQueryChange}
              />
              {searchResult.isFetching && <LoadingComponent />}
              {(searchResult.error != null) && <ErrorComponent message={RtkQueryHelper.mapResponseError(searchResult.error)} />}
              {searchResult.isSuccess && !searchResult.isFetching && (
                <DataGrid
                  rows={searchResult.data}
                  columns={columns}
                  autoHeight={true}
                  disableSelectionOnClick
                  pageSize={5}
                  rowsPerPageOptions={[5]}
                />
              )}
            </>
              )
            : (
            <Alert
              severity="info"
              action={<Button color="inherit" size="small" onClick={() => { setSelectedAssetHolder(null) }}>Change</Button>}
            >
              Selected: <strong>{selectedAssetHolder.firstName} {selectedAssetHolder.lastName}</strong> ({selectedAssetHolder.username}) — id {selectedAssetHolder.id}
            </Alert>
              )}

          <Typography variant={'subtitle1'} sx={{ mt: 3, mb: 2 }}>2. Reason</Typography>
          <TextField
            label="Why is this member excluded from the charge run?"
            multiline
            minRows={2}
            fullWidth
            required
            value={reason}
            onChange={(event) => { setReason(event.target.value) }}
            inputProps={{ maxLength: 1000 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant={'outlined'} type="button" onClick={closeDialog}>
            Close
          </Button>
          <Button variant={'contained'} type="button" disabled={!canSubmit} onClick={handleSubmit}>
            Exclude Member
            {isLoading && <ButtonLoadingComponent/>}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default AddAccountChargeExclusionDialog
